<template>
  <v-menu
    v-model="isOpened"
    :disabled="!userNotifications.length"
    offset="1"
    :width="300"
    :max-height="360"
    :min-height="360"
  >
    <template #activator="{ props: activatorProps }">
      <v-btn
        v-bind="activatorProps"
        id="navbar-user-notifications"
        class="text-none text-subtitle-1 nav-link"
        :disabled="!userNotifications.length"
        size="large"
        variant="text"
        color="primary"
      >
        <div class="icons-container">
          <v-avatar v-if="numUnreadNotifications" class="pa-2 spacebreak-count-badge" size="20">
            <span class="text-white">{{ numUnreadNotifications }}</span>
          </v-avatar>
          <v-icon size="28" icon="mdi-bell-outline" color="primary" />
        </div>
      </v-btn>
    </template>
    <v-list>
      <v-list-subheader class="sub-header">
        {{ $t('general.notifications') }}
      </v-list-subheader>
      <template v-for="notification in userNotifications" :key="notification._id">
        <v-list-item
          :class="getNotificationClass(notification)"
          @click="goToNotification(notification)"
        >
          <v-list-item-title class="notification__title my-1">
            <span>
              <v-avatar
                v-if="!notification.isRead"
                size="9"
                class="notification-unread-badge mr-1"
              />
              {{ $t(`jobNames.${notification.eventType}`) }}
            </span>
            <span>
              {{ formatDate(notification.date, getDateFormats.longWithTimeAndMeridiem) }}
            </span>
          </v-list-item-title>
          <div class="notification__content text-medium-emphasis mb-1">
            {{ getDescription(notification) }}
          </div>
          <div class="notification__content text-medium-emphasis mb-1">
            <b>
              {{
                notification.eventType === workpackageJobTypes.templateSetup
                  ? $t('entities.template')
                  : $t('entities.workpackage')
              }}:
            </b>
            <span>{{ notification.details.entityNames.workpackageName }}</span>
          </div>
          <div v-if="notification.details.entityIds.scenarioId" class="notification__content mb-1">
            <b>{{ $t('entities.scenario') }}: </b>
            <span>{{ notification.details.entityNames.scenarioName }}</span>
          </div>
          <div v-if="notification.details.entityIds.canvasId" class="notification__content mb-1">
            <b>{{ $t('entities.canvas') }}: </b>
            <span>{{ notification.details.entityNames.canvasName }}</span>
          </div>
          <div
            v-if="notification.details.entityIds.checkpointId"
            class="notification__content mb-1"
          >
            <b>{{ $t('entities.checkpoint') }}: </b>
            <span>{{ notification.details.entityNames.checkpointName }}</span>
          </div>
        </v-list-item>
        <v-divider />
      </template>
    </v-list>
  </v-menu>
</template>

<script>
import { ref } from 'vue';
import { get, pick } from 'lodash';
import { mapActions, mapState } from 'pinia';
import jobapi from '@enums/jobapi';
import asyncFunctions from '@enums/asynchronous-functions';
import { useUserNotificationsStore } from '@/stores/user-notifications';
import { useContextStore } from '@/stores/context';
import { useWorkpackagesStore } from '@/stores/workpackages';
import { useDayjs } from '@/composables/use-dayjs';

const functionsConfig = asyncFunctions.functions;

export default {
  setup() {
    const isOpened = ref(false);

    const { formatDate } = useDayjs();
    return {
      isOpened,
      workpackageJobTypes: jobapi.workpackageJobTypes,
      formatDate,
    };
  },

  computed: {
    ...mapState(useUserNotificationsStore, ['userNotifications']),
    ...mapState(useWorkpackagesStore, ['workpackages']),
    ...mapState(useContextStore, ['getDateFormats']),

    unreadNotifications() {
      return this.userNotifications.filter(({ isRead }) => !isRead);
    },

    numUnreadNotifications() {
      // Don't put this greater than 99 without checking size of icon in frontend.
      const limitOfNewNotificationsForIcon = 99;
      return limitOfNewNotificationsForIcon > this.unreadNotifications.length
        ? this.unreadNotifications.length
        : `${limitOfNewNotificationsForIcon}+`;
    },
  },

  watch: {
    // There is no @change event for v-menu, so needed to user watcher to see when the menu is closed
    async isOpened() {
      if (this.isOpened) return;
      const updates = this.userNotifications
        .filter(({ isRead }) => !isRead)
        .map(notification => ({
          filter: { _id: notification._id, isRead: false },
          update: { isRead: true },
        }));
      if (updates.length) await this.updateUserNotifications(updates);
    },
  },

  created() {
    this.fetchUserNotifications();
  },

  methods: {
    ...mapActions(useUserNotificationsStore, ['fetchUserNotifications', 'updateUserNotifications']),

    goToNotification(notification) {
      // TODO: Remove this routeVue3 when all routes are migrated to Vue3
      const routeDetails = get(
        functionsConfig[notification.eventType].notifications,
        'routeVue3',
        'route'
      );
      const params = pick(notification.details.entityIds, routeDetails.paramKeys);
      params.fromNotifications = true;

      this.$router.push({
        name: routeDetails.name,
        params,
        query: this.$route.query,
      });
    },

    getNotificationClass(notification) {
      if (notification.status === jobapi.jobStatuses.failed) return 'notification__failed';
      if (notification.status === jobapi.jobStatuses.finished) return 'notification__success';
      return 'notification__standard';
    },

    getDescription(notification) {
      const eventConfig = functionsConfig[notification.eventType].notifications;
      if (notification.status === jobapi.jobStatuses.failed) {
        const ifProblemPersistsMsg = this.$t('jobErrorNotifications.ifProblemPersists');
        const defaultMsg = this.$t(eventConfig.failed);
        let msg = defaultMsg;

        const errorDetails = notification.error;
        if (errorDetails) {
          const tkey = `jobErrorNotifications.${errorDetails.errorKey}`;
          msg = this.$t(tkey, errorDetails.params);
          // Check if we have a translation, if we do use it, otherwise this is an unknown error
          // show the generic failure message
          if (tkey === msg) {
            msg = defaultMsg;
          }
        }

        return `${msg} ${ifProblemPersistsMsg}`;
      }

      return this.$t(eventConfig[notification.status]);
    },
  },
};
</script>

<style scoped lang="scss">
@import '@style/base/_variables.scss';

.sub-header {
  font-size: 1.5rem;
}

.icons-container {
  position: relative;
}

.spacebreak-count-badge {
  z-index: 2;
  cursor: pointer;
  position: absolute;
  right: -2px;
  top: -6px;
  background-color: $assortment-negative-action-colour;
}

.notification-unread-badge {
  vertical-align: baseline;
  background-color: $assortment-primary-colour;
}

.notification {
  &__title {
    display: flex;
    justify-content: space-between;
    font-size: 1.3rem;
  }

  &__content {
    white-space: initial;
    font-size: 1.1rem;
  }

  &__success {
    border-left: 5px solid $assortment-positive-action-colour;
  }

  &__failed {
    border-left: 5px solid $assortment-negative-action-colour;
  }

  &__standard {
    border-left: 5px solid $assortment-navbar-colour;
  }
}
</style>
